import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, CalendarDays, GraduationCap,
  ClipboardCheck, CreditCard, BarChart3, LogOut, UserCog, Calendar, DollarSign,
  Menu, X
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/",          label: "Dashboard", icon: LayoutDashboard },
  { to: "/coaches",   label: "Coaches",   icon: Users           },
  { to: "/sessions",  label: "Sessions",  icon: CalendarDays    },
  { to: "/kids",      label: "Students",  icon: GraduationCap   },
  { to: "/attendance",label: "Attendance",icon: ClipboardCheck  },
  { to: "/finance", label: "Finance", icon: DollarSign },
  { to: "/analytics", label: "Analytics", icon: BarChart3       },
  { to: "/calendar",  label: "Calendar",  icon: Calendar        },
  { to: "/users",     label: "Users",     icon: UserCog         },
];

const COACH_ITEMS = [
  { to: "/coach-portal", label: "My Portal",  icon: LayoutDashboard },
  { to: "/attendance",   label: "Attendance", icon: ClipboardCheck  },
  { to: "/calendar",     label: "Calendar",   icon: Calendar        },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login", { replace: true }); };
  const isActive = (path) => location.pathname === path;

  const items = user?.role === "admin" ? NAV_ITEMS : COACH_ITEMS;

  return (
    <nav
      style={{
        backgroundColor: "#080F1E",
        borderBottom: "1px solid rgba(0,229,204,0.12)",
        backdropFilter: "blur(12px)",
      }}
      className="sticky top-0 z-50"
    >
      <div className="px-4 sm:px-5 py-0 flex items-center justify-between h-14">
        {/* Logo */}
        <Link to={user?.role === "admin" ? "/" : "/coach-portal"} className="flex items-center gap-2.5 flex-shrink-0" onClick={() => setMenuOpen(false)}>
          <div>
            <img src="/logo512.jpg" alt="FBL" className="h-10 w-auto"/>
          </div>
          <span className="font-extrabold text-white text-base tracking-tight">
            FBL<span style={{ color: "#00E5CC" }}>.</span>
          </span>
        </Link>

        {/* Nav links — desktop */}
        <div className="hidden sm:flex items-center gap-0.5 overflow-x-auto">
          {items.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 whitespace-nowrap"
                style={active
                  ? { backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC" }
                  : { color: "#6B7280" }
                }
              >
                <Icon size={14} strokeWidth={active ? 2.5 : 2} />
                {label}
              </Link>
            );
          })}
        </div>

        {/* User + logout — desktop; hamburger — mobile */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs font-medium text-white leading-tight">
              {user?.profile
                ? `${user.profile.first_name} ${user.profile.last_name}`.trim()
                : user?.coach?.name || "Admin"}
            </p>
            <p className="text-xs" style={{ color: "#00E5CC" }}>{user?.role}</p>
          </div>
          <button
            onClick={handleLogout}
            style={{ backgroundColor: "rgba(255,255,255,0.05)", color: "#9CA3AF" }}
            className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center hover:bg-red-500/20 hover:text-red-400 transition-all"
            title="Logout"
          >
            <LogOut size={15} />
          </button>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{ backgroundColor: "rgba(255,255,255,0.05)", color: "#9CA3AF" }}
            className="sm:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-all"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Backdrop — closes the mobile dropdown on outside tap */}
      {menuOpen && (
        <div className="sm:hidden fixed inset-0 z-40" style={{ top: "3.5rem" }}
          onClick={() => setMenuOpen(false)} />
      )}

      {/* Nav links — mobile dropdown */}
      {menuOpen && (
        <div
          style={{ backgroundColor: "#080F1E", borderTop: "1px solid rgba(0,229,204,0.12)" }}
          className="sm:hidden relative z-50 px-3 py-2 space-y-0.5 max-h-[calc(100vh-3.5rem)] overflow-y-auto"
        >
          {items.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-150"
                style={active
                  ? { backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC" }
                  : { color: "#9CA3AF" }
                }
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                {label}
              </Link>
            );
          })}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }} className="mt-2 pt-2">
            <div className="px-3 py-1.5">
              <p className="text-sm font-medium text-white leading-tight">
                {user?.profile
                  ? `${user.profile.first_name} ${user.profile.last_name}`.trim()
                  : user?.coach?.name || "Admin"}
              </p>
              <p className="text-xs" style={{ color: "#00E5CC" }}>{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
